import { getToken, deleteToken, onMessage, MessagePayload, Messaging } from 'firebase/messaging';
import { messaging } from '../firebase';
import { db, auth } from '../firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { resolveDeepLink } from '../utils/deepLink';

/**
 * localStorage key recording which uid this browser's FCM token was last
 * registered for. An FCM token belongs to the browser, not the person: if
 * student A's session ended without a clean logout (another tab signed out,
 * the session was revoked, the network dropped mid-cleanup) and student B
 * then signs in on the same school computer, getToken hands B the very same
 * token - still listed on A's profile, so A's pushes would keep landing here.
 * When the recorded owner is someone else, the token is rotated first.
 */
export const FCM_TOKEN_OWNER_KEY = 'limelight_fcm_token_owner';

const readTokenOwner = (): string | null => {
  try {
    return window.localStorage.getItem(FCM_TOKEN_OWNER_KEY);
  } catch {
    return null;
  }
};

const writeTokenOwner = (uid: string | null): void => {
  try {
    if (uid) window.localStorage.setItem(FCM_TOKEN_OWNER_KEY, uid);
    else window.localStorage.removeItem(FCM_TOKEN_OWNER_KEY);
  } catch {
    // Storage blocked - the logout path still deletes the token itself.
  }
};

/**
 * Web notification service for handling FCM push notifications
 * Provides similar functionality to the mobile app's NotificationService
 */
class WebNotificationService {
  private messaging: Messaging | null = null;
  // This browser's current FCM token, cached so logout cleanup can remove
  // exactly this device's entry.
  private currentToken: string | null = null;
  // Optional React-Router navigation callback, registered by
  // NotificationNavigationBridge (inside <Router>) so notification clicks route
  // through the SPA instead of a hash the BrowserRouter ignores.
  private navigate: ((path: string) => void) | null = null;

  setNavigationHandler(handler: ((path: string) => void) | null): void {
    this.navigate = handler;
  }
  private vapidKey = 'BMTLy80uCmvrgSCbi5te9pvOYcvVYzFhzktk0K3nSCcVkZuaAdRN8gPKDzd2xiKBVxFqTqMhifiypNt_vVQE2YI';
  private initialized = false;
  // The profile snapshot that triggers initialize() can fire twice in quick
  // succession; a second run would race the first through the token rotation
  // (deleting the token the first run just minted). Concurrent callers share
  // the one in-flight run instead.
  private initializing: Promise<void> | null = null;
  // onMessage registers a listener each call; initialize() runs again for
  // each sign-in in the same tab, so the handler is attached once only.
  private foregroundHandlerAttached = false;
  private permissionState: NotificationPermission = 'default';

  /**
   * Initialize the notification service
   */
  initialize(): Promise<void> {
    if (!this.initializing) {
      this.initializing = this.runInitialize().finally(() => {
        this.initializing = null;
      });
    }
    return this.initializing;
  }

  private async runInitialize(): Promise<void> {
    try {
      console.log('🔔 Initializing web notification service...');
      
      // Check if browser supports notifications
      if (!('Notification' in window)) {
        console.warn('❌ This browser does not support notifications');
        return;
      }

      // Get messaging instance
      this.messaging = await messaging;
      if (!this.messaging) {
        console.warn('❌ FCM messaging not supported');
        return;
      }

      // Set up foreground message handler
      this.setupForegroundMessageHandler();
      
      // Check current permission state
      this.permissionState = Notification.permission;
      
      // If already granted, get token and register
      if (this.permissionState === 'granted') {
        await this.updateFCMToken();
      }

      this.initialized = true;
      console.log('✅ Web notification service initialized successfully');

    } catch (error) {
      console.error('❌ Error initializing web notification service:', error);
    }
  }

  /**
   * Request notification permission (must be called from user gesture)
   * Returns true if permission granted, false otherwise
   */
  async requestPermission(): Promise<boolean> {
    try {
      if (!this.messaging) {
        console.warn('❌ FCM messaging not available');
        return false;
      }

      // Skip if permission already denied to avoid annoying users
      if (this.permissionState === 'denied') {
        console.log('🔇 Notification permission previously denied - skipping');
        return false;
      }

      console.log('🔐 Requesting notification permission...');
      
      // Request permission (must be from user gesture)
      const permission = await Notification.requestPermission();
      this.permissionState = permission;
      
      if (permission === 'granted') {
        console.log('✅ Notification permission granted');
        await this.updateFCMToken();
        return true;
      } else {
        console.log('🔇 Notification permission declined');
        return false;
      }
      
    } catch (error) {
      console.error('❌ Error requesting notification permission:', error);
      return false;
    }
  }

  /**
   * Get current FCM token and update user profile
   */
  private async updateFCMToken(): Promise<void> {
    try {
      if (!this.messaging || !auth.currentUser) {
        return;
      }

      console.log('🎯 Getting FCM token...');

      // This browser's token was last registered for someone else: rotate it
      // so the entry left on their profile goes dead instead of routing their
      // notifications to whoever is signed in now.
      const owner = readTokenOwner();
      if (owner && owner !== auth.currentUser.uid) {
        try {
          await deleteToken(this.messaging);
        } catch (e) {
          // Rotation failed, so getToken would hand back the previous
          // person's token. Registering it here would route their pushes to
          // whoever is signed in now - skip registration instead, and leave
          // the owner recorded so the next sign-in retries the rotation.
          console.warn('Could not rotate FCM token from previous user - not registering this browser:', e);
          return;
        }
        writeTokenOwner(null);
      }
      
      const token = await getToken(this.messaging, {
        vapidKey: this.vapidKey
      });

      if (token) {
        console.log('✅ FCM token obtained:', token.substring(0, 20) + '...');
        await this.saveFCMToken(token);
      } else {
        console.warn('❌ No FCM token available');
      }
      
    } catch (error) {
      console.error('❌ Error getting FCM token:', error);
    }
  }

  /**
   * Save FCM token to user profile
   */
  private async saveFCMToken(token: string): Promise<void> {
    try {
      if (!auth.currentUser) {
        console.warn('❌ No authenticated user to save token');
        return;
      }

      const userProfileRef = doc(db, 'profiles', auth.currentUser.uid);

      // Read-filter-write by token string. The previous
      // arrayRemove({platform:'web'}) never matched the stored
      // {token, platform, lastUpdated, userAgent} objects (Firestore
      // arrayRemove needs a full-object match), so web tokens accumulated
      // forever and logout cleanup was a no-op (audit H21). Drop userAgent -
      // it was unused fingerprinting.
      const snap = await getDoc(userProfileRef);
      const existing = (snap.data()?.fcmTokens as any[] | undefined) ?? [];
      const filtered = existing.filter(
        (t) => t && typeof t === 'object' && t.token !== token
      );
      filtered.push({ token, platform: 'web', updatedAt: new Date() });

      await updateDoc(userProfileRef, { fcmTokens: filtered });
      this.currentToken = token;
      writeTokenOwner(auth.currentUser.uid);

      console.log('✅ FCM token saved to user profile');

    } catch (error) {
      console.error('❌ Error saving FCM token:', error);
    }
  }

  /**
   * Set up handler for foreground messages
   */
  private setupForegroundMessageHandler(): void {
    if (!this.messaging || this.foregroundHandlerAttached) return;
    this.foregroundHandlerAttached = true;

    onMessage(this.messaging, (payload) => {
      console.log('📱 Foreground message received:', payload);
      
      // Show browser notification for foreground messages
      this.showBrowserNotification(payload);
    });
  }

  /**
   * Show browser notification for foreground messages
   */
  private showBrowserNotification(payload: MessagePayload): void {
    try {
      if (this.permissionState !== 'granted') {
        console.warn('❌ Cannot show notification - permission not granted');
        return;
      }

      const { notification, data } = payload;
      
      if (!notification?.title) {
        console.warn('❌ No notification title provided');
        return;
      }

      const options: NotificationOptions = {
        body: notification.body || '',
        icon: '/logo192.png', // Your app icon
        badge: '/logo192.png',
        tag: data?.type || 'general', // Prevent duplicate notifications
        requireInteraction: false,
        data: data, // Pass through data for click handling
      };

      // Show the notification
      const browserNotification = new Notification(notification.title, options);
      
      // Handle notification click
      browserNotification.onclick = (event) => {
        event.preventDefault();
        browserNotification.close();
        
        // Focus the window
        window.focus();
        
        // Handle deep linking if provided
        if (data?.deepLink) {
          this.handleDeepLink(data.deepLink, data);
        }
      };

      // Auto-close after 5 seconds
      setTimeout(() => {
        browserNotification.close();
      }, 5000);

    } catch (error) {
      console.error('❌ Error showing browser notification:', error);
    }
  }

  /**
   * Handle deep linking from notifications
   */
  private handleDeepLink(deepLink: string, data: any): void {
    try {
      console.log('🔗 Handling notification deep link:', deepLink, data);
      
      // The app uses BrowserRouter, so a URL hash was ignored and notification
      // clicks never navigated (audit web deep-link finding). Route through the
      // registered React-Router handler; fall back to a real path navigation.
      const target = resolveDeepLink(deepLink, data?.showId);

      if (this.navigate) {
        this.navigate(target);
      } else {
        window.location.assign(target);
      }

    } catch (error) {
      console.error('❌ Error handling deep link:', error);
    }
  }

  /**
   * Send a test notification (for debugging)
   */
  async sendTestNotification(): Promise<void> {
    try {
      console.log('🧪 Sending test notification...');
      
      // Create a mock notification payload with all required properties
      const testPayload: MessagePayload = {
        from: 'test',
        collapseKey: 'test',
        messageId: 'test-' + Date.now(),
        notification: {
          title: '🧪 Test Notification',
          body: 'This is a test notification from the web app!'
        },
        data: {
          type: 'test',
          showId: 'test_show',
          targetId: 'test_target',
          deepLink: '/test',
          timestamp: Date.now().toString()
        }
      };

      // Show the notification directly (simulating a foreground message)
      this.showBrowserNotification(testPayload);
      
      console.log('✅ Test notification sent');
      
    } catch (error) {
      console.error('❌ Error sending test notification:', error);
      throw error;
    }
  }

  /**
   * Get notification status for debugging
   */
  getNotificationStatus(): {
    hasPermission: boolean;
    hasAskedPermission: boolean;
    permissionState: NotificationPermission;
    isInitialized: boolean;
    browserSupported: boolean;
    fcmSupported: boolean;
  } {
    return {
      hasPermission: this.permissionState === 'granted',
      hasAskedPermission: localStorage.getItem('notification_permission_asked') === 'true',
      permissionState: this.permissionState,
      isInitialized: this.initialized,
      browserSupported: 'Notification' in window,
      fcmSupported: this.messaging !== null,
    };
  }

  /**
   * Check if we should ask for permission
   */
  shouldAskForPermission(): boolean {
    // Don't ask if already granted or denied
    if (this.permissionState === 'granted' || this.permissionState === 'denied') {
      return false;
    }
    
    // Don't ask if we've asked before (respect "don't ask again")
    if (localStorage.getItem('notification_permission_asked') === 'true') {
      return false;
    }
    
    return true;
  }

  /**
   * Mark that we won't ask again
   */
  setDontAskAgain(): void {
    localStorage.setItem('notification_permission_asked', 'true');
    localStorage.setItem('notification_dont_ask_again', 'true');
  }

  /**
   * Check if user chose "don't ask again"
   */
  isDontAskAgain(): boolean {
    return localStorage.getItem('notification_dont_ask_again') === 'true';
  }

  /**
   * Clean up FCM token on logout.
   *
   * Two independent halves, and the second must run even when the first
   * cannot: removing this browser's entry from the profile (needs the user
   * still signed in and Firestore reachable), and deleting the token from FCM
   * (needs neither). Deleting the token is what actually stops this browser
   * receiving the previous person's pushes on a shared school computer - a
   * dead token left on a profile is pruned by the sender on first use.
   */
  async cleanup(): Promise<void> {
    console.log('🧹 Cleaning up FCM token...');

    // Logout can run before initialize() resolved (a quick sign-out after
    // load), so resolve the messaging instance here rather than trusting it.
    if (!this.messaging) {
      try {
        this.messaging = await messaging;
      } catch {
        this.messaging = null;
      }
    }

    // Resolve this browser's token (cached from save, else read live). Only
    // read it live when permission is already granted: getToken would
    // otherwise prompt, and it needs the same VAPID key the token was minted
    // with or it returns a different subscription.
    let token = this.currentToken;
    if (!token && this.messaging && typeof Notification !== 'undefined' && Notification.permission === 'granted') {
      try {
        token = await getToken(this.messaging, { vapidKey: this.vapidKey });
      } catch {
        token = null;
      }
    }

    try {
      if (token && auth.currentUser) {
        const userProfileRef = doc(db, 'profiles', auth.currentUser.uid);
        const snap = await getDoc(userProfileRef);
        const existing = (snap.data()?.fcmTokens as any[] | undefined) ?? [];
        const filtered = existing.filter(
          (t) => t && typeof t === 'object' && t.token !== token
        );
        if (filtered.length !== existing.length) {
          await updateDoc(userProfileRef, { fcmTokens: filtered });
        }
      }
    } catch (error) {
      console.error('❌ Error removing FCM token from profile:', error);
    }

    // Delete the token from FCM so this browser stops being a push target.
    let deleted = true;
    if (this.messaging) {
      try {
        await deleteToken(this.messaging);
      } catch (e) {
        console.warn('Could not delete FCM token:', e);
        deleted = false;
      }
    }
    this.currentToken = null;
    if (deleted) {
      writeTokenOwner(null);
    } else if (!readTokenOwner() && auth.currentUser) {
      // The token is still live and still this person's. Keep (or record)
      // them as its owner so the next person to sign in here rotates it
      // rather than inheriting it along with this person's pushes.
      writeTokenOwner(auth.currentUser.uid);
    }
    // The next person to sign in on this tab gets their own token minted,
    // rather than inheriting "already initialised" and receiving nothing.
    this.initialized = false;

    console.log('✅ FCM token cleanup completed');
  }
}

// Export singleton instance
export const notificationService = new WebNotificationService();
export default WebNotificationService;